import { useEffect, useState } from "react";
import { supabase } from "../supabase";

export default function Subscriptions() {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setProfiles(data);
    setLoading(false);
  };

  const startEdit = (profile: any) => {
    setEditing(profile.id);
    setEditData({
      subscription_tier: profile.subscription_tier,
      subscription_expires_at: profile.subscription_expires_at
        ? new Date(profile.subscription_expires_at).toISOString().split("T")[0]
        : "",
    });
  };

  const saveEdit = async (id: string) => {
    setSaving(true);
    const expires = editData.subscription_expires_at
      ? new Date(editData.subscription_expires_at).toISOString()
      : null;
    await supabase
      .from("profiles")
      .update({
        subscription_tier: editData.subscription_tier,
        subscription_expires_at: expires,
      })
      .eq("id", id);
    setEditing(null);
    fetchProfiles();
    setSaving(false);
  };

  const giveQuickAccess = async (id: string, tier: string, days: number) => {
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    await supabase
      .from("profiles")
      .update({
        subscription_tier: tier,
        subscription_expires_at: expires.toISOString(),
      })
      .eq("id", id);
    fetchProfiles();
  };

  const getTierColor = (tier: string) => {
    if (tier === "pro") return "#9b59b6";
    if (tier === "basic") return "#3498db";
    return "#95a5a6";
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("ru-RU");
  };

  const isExpired = (dateStr: string) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const filtered = profiles.filter((p) =>
    p.id.toLowerCase().includes(search.toLowerCase()),
  );

  if (loading) return <div style={{ padding: 32 }}>Загрузка...</div>;

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>💳 Управление подписками</h2>
        <div style={styles.stats}>
          <div style={styles.statCard}>
            <div style={styles.statNum}>
              {profiles.filter((p) => p.subscription_tier === "pro").length}
            </div>
            <div style={styles.statLabel}>Pro</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNum}>
              {profiles.filter((p) => p.subscription_tier === "basic").length}
            </div>
            <div style={styles.statLabel}>Basic</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNum}>
              {profiles.filter((p) => p.subscription_tier === "free").length}
            </div>
            <div style={styles.statLabel}>Free</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNum}>{profiles.length}</div>
            <div style={styles.statLabel}>Всего</div>
          </div>
        </div>
      </div>

      <input
        style={styles.search}
        placeholder="Поиск по User ID..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.thead}>
              <th style={styles.th}>User ID</th>
              <th style={styles.th}>Тариф</th>
              <th style={styles.th}>Истекает</th>
              <th style={styles.th}>AI запросы</th>
              <th style={styles.th}>Быстрые акции</th>
              <th style={styles.th}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((profile) => (
              <tr key={profile.id} style={styles.tr}>
                <td style={styles.td}>
                  <span style={styles.userId}>
                    {profile.id.substring(0, 12)}...
                  </span>
                </td>
                <td style={styles.td}>
                  {editing === profile.id ? (
                    <select
                      style={styles.select}
                      value={editData.subscription_tier}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          subscription_tier: e.target.value,
                        })
                      }
                    >
                      <option value="free">Free</option>
                      <option value="basic">Basic</option>
                      <option value="pro">Pro</option>
                    </select>
                  ) : (
                    <span
                      style={{
                        ...styles.tierBadge,
                        backgroundColor: getTierColor(
                          profile.subscription_tier,
                        ),
                      }}
                    >
                      {profile.subscription_tier?.toUpperCase()}
                    </span>
                  )}
                </td>
                <td style={styles.td}>
                  {editing === profile.id ? (
                    <input
                      type="date"
                      style={styles.dateInput}
                      value={editData.subscription_expires_at}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          subscription_expires_at: e.target.value,
                        })
                      }
                    />
                  ) : (
                    <span
                      style={{
                        color: isExpired(profile.subscription_expires_at)
                          ? "#e74c3c"
                          : "#222",
                      }}
                    >
                      {formatDate(profile.subscription_expires_at)}
                      {isExpired(profile.subscription_expires_at) && " ⚠️"}
                    </span>
                  )}
                </td>
                <td style={styles.td}>{profile.ai_requests_count || 0}</td>
                <td style={styles.td}>
                  <div style={styles.quickBtns}>
                    <button
                      style={styles.quickBtn}
                      onClick={() => giveQuickAccess(profile.id, "pro", 7)}
                    >
                      Pro 7д
                    </button>
                    <button
                      style={styles.quickBtn}
                      onClick={() => giveQuickAccess(profile.id, "pro", 30)}
                    >
                      Pro 30д
                    </button>
                    <button
                      style={styles.quickBtn}
                      onClick={() => giveQuickAccess(profile.id, "basic", 30)}
                    >
                      Basic 30д
                    </button>
                    <button
                      style={{ ...styles.quickBtn, backgroundColor: "#e74c3c" }}
                      onClick={() => giveQuickAccess(profile.id, "free", 0)}
                    >
                      Сбросить
                    </button>
                  </div>
                </td>
                <td style={styles.td}>
                  {editing === profile.id ? (
                    <div style={styles.editBtns}>
                      <button
                        style={styles.saveBtn}
                        onClick={() => saveEdit(profile.id)}
                        disabled={saving}
                      >
                        {saving ? "..." : "✓"}
                      </button>
                      <button
                        style={styles.cancelBtn}
                        onClick={() => setEditing(null)}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      style={styles.editBtn}
                      onClick={() => startEdit(profile)}
                    >
                      ✏️
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: "#222", marginBottom: 16 },
  stats: { display: "flex", gap: 12, marginBottom: 8 },
  statCard: {
    backgroundColor: "#fff",
    borderRadius: 10,
    padding: "12px 20px",
    textAlign: "center",
    border: "1px solid #eee",
    minWidth: 80,
  },
  statNum: { fontSize: 24, fontWeight: 700, color: "#2ECC71" },
  statLabel: { fontSize: 12, color: "#888", marginTop: 2 },
  search: {
    width: "100%",
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #eee",
    fontSize: 14,
    marginBottom: 16,
    boxSizing: "border-box" as const,
  },
  tableWrap: {
    backgroundColor: "#fff",
    borderRadius: 12,
    border: "1px solid #eee",
    overflow: "auto",
  },
  table: { width: "100%", borderCollapse: "collapse" as const },
  thead: { backgroundColor: "#f9f9f9" },
  th: {
    padding: "12px 16px",
    textAlign: "left" as const,
    fontSize: 13,
    fontWeight: 600,
    color: "#555",
    borderBottom: "1px solid #eee",
  },
  tr: { borderBottom: "1px solid #f0f0f0" },
  td: {
    padding: "12px 16px",
    fontSize: 13,
    color: "#222",
    verticalAlign: "middle" as const,
  },
  userId: { fontFamily: "monospace", fontSize: 12, color: "#888" },
  tierBadge: {
    fontSize: 11,
    padding: "3px 10px",
    borderRadius: 20,
    color: "#fff",
    fontWeight: 700,
  },
  select: {
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #ddd",
    fontSize: 13,
  },
  dateInput: {
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #ddd",
    fontSize: 13,
  },
  quickBtns: { display: "flex", gap: 4, flexWrap: "wrap" as const },
  quickBtn: {
    padding: "4px 8px",
    borderRadius: 6,
    border: "none",
    backgroundColor: "#2ECC71",
    color: "#fff",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 600,
  },
  editBtns: { display: "flex", gap: 4 },
  saveBtn: {
    padding: "6px 10px",
    backgroundColor: "#2ECC71",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
  },
  cancelBtn: {
    padding: "6px 10px",
    backgroundColor: "#eee",
    color: "#555",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  editBtn: {
    padding: "6px 10px",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 6,
    cursor: "pointer",
  },
};
